import { useEffect, useRef, useCallback, useState } from 'react';

import Pusher, { Channel } from 'pusher-js';
import { toast } from 'react-toastify';

import { WEBSOCKET_KEY } from '@app/settings/base-url';
import useActions from '@app/store/hooks/useActions';
import { useOrdersExportsDownloadMutation, IOrderExportQuery } from '@entities/orders';
import { DownloadWebSocketMessage } from '@shared/types/download';

let pusherInstance: Pusher | null = null;
const PUSHER_CLUSTER = 'ap2';
const DOWNLOAD_READY_EVENT = 'order.export.status.completed';
const DOWNLOAD_PROGRESS_EVENT = 'order.export.status.processing';
const DOWNLOAD_FAILED_EVENT = 'order.export.status.failed';
const makeChannelName = (id: string) => `exports.${id}`;

interface UseWebSocketDownloadOptions {
  onDownloadComplete?: (downloadUrl: string) => void;
  onDownloadError?: (error: string) => void;
}

export const useWebSocketDownload = (options: UseWebSocketDownloadOptions = {}) => {
  const { startDownload, updateDownloadProgress, completeDownload } = useActions();
  const initializedThisInstance = useRef(false);
  const channelRef = useRef<Channel | null>(null);
  const downloadIdRef = useRef<string | null>(null);

  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);

  const [ordersExportsDownload] = useOrdersExportsDownloadMutation();

  const { onDownloadComplete, onDownloadError } = options;

  const handleDownloadProgress = useCallback(
    (data: DownloadWebSocketMessage) => {
      if (data.status === 'processing') {
        console.log('received download progress event');
        updateDownloadProgress({
          chunks: data.export.chunks,
          chunks_processed: data.export.chunks_processed,
          total: data.export.total,
        });
      }
    },
    [updateDownloadProgress],
  );

  const handleDownloadReady = useCallback(
    (data: DownloadWebSocketMessage) => {
      console.log('received download ready event');
      completeDownload();
      setIsError(false);
      setError(null);
      if (onDownloadComplete) {
        onDownloadComplete(data.export.download_url);
      } else {
        window.open(data.export.download_url, '_blank');
      }
    },
    [completeDownload, onDownloadComplete],
  );

  const handleDownloadFailed = useCallback(
    (data: DownloadWebSocketMessage) => {
      console.log('received download failed event');
      completeDownload();
      const errorMessage = 'Download failed. Please try again.';
      setIsError(true);
      setError({ data: { message: errorMessage } });
      if (onDownloadError) {
        onDownloadError(errorMessage);
      }
      toast.error(errorMessage);
    },
    [completeDownload, onDownloadError],
  );

  const subscribeToDownload = useCallback(
    (downloadId: string) => {
      if (!pusherInstance) {
        pusherInstance = new Pusher(WEBSOCKET_KEY, {
          cluster: PUSHER_CLUSTER,
          forceTLS: false,
          enabledTransports: ['ws', 'wss'],
        });
      }

      const pusher = pusherInstance;
      const channelName = makeChannelName(downloadId);
      downloadIdRef.current = downloadId;

      const handleError = (err: unknown) => {
        console.error('Pusher connection error:', err);
        completeDownload();
        setIsError(true);
        setError({ data: { message: 'Connection error during download' } });
        toast.error('Connection error during download');
      };

      const subscribeAndBind = () => {
        if (channelRef.current && channelRef.current.name === channelName) {
          return;
        }

        const channel = pusher.subscribe(channelName);
        channelRef.current = channel;

        channel.bind('pusher:subscription_succeeded', () => {
          console.log('Subscribed to download channel:', channelName);
          channel.bind(DOWNLOAD_PROGRESS_EVENT, handleDownloadProgress);
          channel.bind(DOWNLOAD_READY_EVENT, handleDownloadReady);
          channel.bind(DOWNLOAD_FAILED_EVENT, handleDownloadFailed);
        });

        channel.bind('pusher:subscription_error', (status: unknown) => {
          console.error(`Failed to subscribe to channel ${channelName}:`, status);
          channelRef.current = null;
          completeDownload();
          setIsError(true);
          setError({ data: { message: 'Failed to subscribe to download updates' } });
        });
      };

      const handleConnected = () => {
        subscribeAndBind();
      };

      pusher.connection.bind('connected', handleConnected);
      pusher.connection.bind('error', handleError);

      if (pusher.connection.state === 'connected') {
        subscribeAndBind();
      }
    },
    [handleDownloadProgress, handleDownloadReady, handleDownloadFailed, completeDownload],
  );

  const initiateOrdersExportDownload = useCallback(
    async (query: IOrderExportQuery, type: 'deposits' | 'withdrawals') => {
      try {
        startDownload();
        setIsError(false);
        setError(null);

        toast.info('Download scheduled and will be available shortly', {
          autoClose: 2000,
        });

        const response = await ordersExportsDownload({ ...query, type });

        if ('data' in response && response.data) {
          const downloadData = response.data.data;
          const downloadId = downloadData.id;
          if (downloadId) {
            subscribeToDownload(downloadId);
          } else {
            throw new Error('No download ID received from server');
          }
        } else {
          throw new Error('No response data received from server');
        }
      } catch (error) {
        completeDownload();
        console.error('Error initiating orders export download:', error);
        setIsError(true);
        setError(error);
        toast.error('Failed to initiate download');
      }
    },
    [startDownload, completeDownload, subscribeToDownload, ordersExportsDownload],
  );

  const disconnect = useCallback(() => {
    if (pusherInstance && channelRef.current) {
      pusherInstance.unsubscribe(channelRef.current.name);
      channelRef.current = null;
    }
    downloadIdRef.current = null;
    completeDownload();
  }, [completeDownload]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      initializedThisInstance.current = false;

      if (pusherInstance && channelRef.current) {
        pusherInstance.unsubscribe(channelRef.current.name);
        channelRef.current = null;
      }
    };
  }, []);

  return {
    initiateOrdersExportDownload,
    disconnect,
    isConnected: pusherInstance?.connection.state === 'connected',
    downloadId: downloadIdRef.current,
    isError,
    error,
  };
};
